import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  ApprovalsMutationsCreateApprovalsApprovalFlowArgs as ApprovalFlowArgs,
  CreateApprovalsApprovalFlowApproversInput as ApproverArgs,
  CreateApprovalsApprovalFlowStepsInput as StepArgs,
} from 'generated/resources/graphql'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { useApprovalFlowForm } from 'modules/approvals/hooks/useApprovalFlowForm'

import AddApproverModal from './AddApproverModal'
import { AddStepButton } from './AddStepButton'
import { ApprovalStep } from './ApprovalStep'
import { Wrapper } from './Wrapper'

interface Props {
  approvalFlow: ApprovalFlowArgs | null
  showAddModal?: boolean
  canEdit: boolean
  canAddSteps: boolean
  canDeleteSteps: boolean
  showBorder?: boolean
  onChange: (approvalFlow: ApprovalFlowArgs) => void
  onCloseModal?: () => void
}

export const ApprovalFlowForm = ({
  approvalFlow,
  showAddModal,
  canEdit,
  canAddSteps,
  canDeleteSteps,
  showBorder,
  onChange,
  onCloseModal,
}: Props) => {
  const {
    approvalFlowData,
    addApproverToStep,
    deleteStep,
    addStep,
    updateStep,
    removeApproverFromStep,
  } = useApprovalFlowForm(approvalFlow ?? null)

  const [showAddApprover, setshowAddApprover] = React.useState(showAddModal)
  const [selectedStep, setSelectedStep] = React.useState<StepArgs | null>(null)

  const openModalToAddApprover = (step: StepArgs | null) => {
    setshowAddApprover(true)
    setSelectedStep(step)
  }

  const closeModalToAddApprover = () => {
    setshowAddApprover(false)
    setSelectedStep(null)
    if (onCloseModal) {
      onCloseModal()
    }
  }

  const addApprover = (step: StepArgs | null, approver: ApproverArgs) => {
    if (step) {
      return addApproverToStep(step.position, approver)
    }
    return addStep(approver)
  }

  useEffect(() => {
    setshowAddApprover(showAddModal)
  }, [showAddModal])

  useUnsafeEffect(() => {
    onChange(approvalFlowData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalFlowData])

  return (
    <>
      <Box gap={showBorder ? 's16' : 's0'}>
        <Wrapper showBorder={showBorder}>
          {approvalFlowData.steps.map((step, index) => {
            // Show delete button if there are more than one step, when there is only one step it can't be deleted.
            const showDeleteButton = canDeleteSteps && approvalFlowData.steps.length > 1

            return (
              <ApprovalStep
                onAddApprover={(stepToAdd) => openModalToAddApprover(stepToAdd)}
                onUpdateStep={(updatedStep) => updateStep(updatedStep)}
                onDeleteStep={deleteStep}
                removeApproverFromStep={removeApproverFromStep}
                key={index}
                step={step}
                index={index}
                canEdit={canEdit}
                canDeleteSteps={showDeleteButton}
              />
            )
          })}
        </Wrapper>
        {canAddSteps && <AddStepButton addStep={() => openModalToAddApprover(null)} />}
      </Box>
      {showAddApprover && (
        <AddApproverModal
          onClose={() => closeModalToAddApprover()}
          addApprover={(approver) => addApprover(selectedStep, approver)}
        />
      )}
    </>
  )
}
